import React, { useState, useRef, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import useSound from "use-sound";

import Popover from "@mui/material/Popover";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import { makeStyles } from "@mui/styles";
import Badge from "@mui/material/Badge";
import ChatIcon from "@mui/icons-material/Chat";

import TicketListItem from "../TicketListItem";
import { i18n } from "../../translate/i18n";
import useTickets from "../../hooks/useTickets";
import alertSound from "../../assets/sound.mp3";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useSocket } from "../../context/Socket/SocketContext";

const useStyles = makeStyles(theme => ({
	tabContainer: {
		overflowY: "auto",
		maxHeight: 350,
		...theme.scrollbarStyles,
	},
	popoverPaper: {
		width: "100%",
		maxWidth: 350,
		marginLeft: theme.spacing(2),
		marginRight: theme.spacing(1),
		[theme.breakpoints.down("sm")]: {
			maxWidth: 270,
		},
	},
	noShadow: {
		boxShadow: "none !important",
	},
	iconButton: {
		color: theme.palette.text.primary,
	},
	badge: {
		"& .MuiBadge-badge": {
			backgroundColor: "#d32f2f", // Rojo estándar para notificaciones
			color: "white",
		},
	},
}));

import { useLocation } from "react-router-dom";

const mediaNotificationLabels = {
	image: "Imagen",
	video: "Video",
	audio: "Audio",
	ptt: "Nota de voz",
	document: "Documento",
	sticker: "Sticker",
	location: "Ubicacion",
	vcard: "Contacto",
};

const getNotificationMessageBody = message => {
	const rawBody = String(message?.body || "");
	const isForwarded = rawBody.startsWith("\u200B\u200B\u200B");
	const cleanBody = rawBody
		.replace(/^\u200B\u200B\u200B/, "")
		.replace(/^[\u200B-\u200D\u2060\uFEFF\u00A0]+/u, "")
		.trim();
	const mediaLabel = mediaNotificationLabels[message?.mediaType];
	const isTechnicalMediaBody = /^\[(image|video|audio|ptt|sticker|archivo)\]$/i.test(
		cleanBody
	);
	const prefix = isForwarded ? "Reenviado · " : "";

	if (mediaLabel) {
		if (!cleanBody || isTechnicalMediaBody) {
			return `${prefix}${mediaLabel}`;
		}

		return `${prefix}${mediaLabel}: ${cleanBody}`;
	}

	return `${prefix}${cleanBody || "Nuevo mensaje"}`;
};

const NotificationsPopOver = () => {
	const classes = useStyles();
	const navigate = useNavigate();
	const location = useLocation();
	const { user } = useContext(AuthContext);
	const { socket, syncVersion } = useSocket();
	const ticketIdUrl = location.pathname.split("/")[2] || "";
	const ticketIdRef = useRef(ticketIdUrl);
	const anchorEl = useRef();
	const [isOpen, setIsOpen] = useState(false);
	const [notifications, setNotifications] = useState([]);

	const [, setDesktopNotifications] = useState([]);

	const { tickets } = useTickets({ withUnreadMessages: "true", refreshVersion: syncVersion });
	const [play] = useSound(alertSound);
	const soundAlertRef = useRef();
	const navigateRef = useRef(navigate);

	useEffect(() => {
		navigateRef.current = navigate;
	}, [navigate]);

	useEffect(() => {
		soundAlertRef.current = play;

		if (!("Notification" in window)) {
			console.log("This browser doesn't support notifications");
		} else {
			Notification.requestPermission();
		}
	}, [play]);

	useEffect(() => {
		setNotifications(tickets);
	}, [tickets]);

	useEffect(() => {
		ticketIdRef.current = ticketIdUrl;
	}, [ticketIdUrl]);

	useEffect(() => {
		if (!socket) {
			return undefined;
		}

		const handleConnect = () => socket.emit("joinNotification");

		const handleTicket = data => {
			if (data.action === "updateUnread" || data.action === "delete") {
				setNotifications(prevState => {
					const ticketIndex = prevState.findIndex(t => t.id === data.ticketId);
					if (ticketIndex !== -1) {
						prevState.splice(ticketIndex, 1);
						return [...prevState];
					}
					return prevState;
				});

				setDesktopNotifications(prevState => {
					const notfiticationIndex = prevState.findIndex(
						n => n.tag === String(data.ticketId)
					);
					if (notfiticationIndex !== -1) {
						prevState[notfiticationIndex].close();
						prevState.splice(notfiticationIndex, 1);
						return [...prevState];
					}
					return prevState;
				});
			}
		};

		const handleAppMessage = data => {
			if (
				data.action === "create" &&
				!data.message.read &&
				(data.ticket.userId === user?.id || !data.ticket.userId)
			) {
				setNotifications(prevState => {
					const ticketIndex = prevState.findIndex(t => t.id === data.ticket.id);
					if (ticketIndex !== -1) {
						prevState[ticketIndex] = data.ticket;
						return [...prevState];
					}
					return [data.ticket, ...prevState];
				});

				const shouldNotNotificate =
					(data.ticket.publicId === ticketIdRef.current &&
						document.visibilityState === "visible") ||
					(data.ticket.userId && data.ticket.userId !== user?.id) ||
					data.ticket.isGroup;

				if (shouldNotNotificate) return;

				handleNotifications(data);
				return;
			}

		};

		if (socket.connected) {
			socket.emit("joinNotification");
		}

		socket.on("connect", handleConnect);
		socket.on("ticket", handleTicket);
		socket.on("appMessage", handleAppMessage);

		return () => {
			socket.off("connect", handleConnect);
			socket.off("ticket", handleTicket);
			socket.off("appMessage", handleAppMessage);
		};
	}, [socket, user]);

	const handleNotifications = data => {
		const { message, contact, ticket } = data;
		const messageBody = getNotificationMessageBody(message);

		const options = {
			body: `${messageBody} - ${format(new Date(), "HH:mm")}`,
			icon: contact.profilePicUrl,
			tag: ticket.id,
			renotify: true,
		};

		const notification = new Notification(
			`${i18n.t("tickets.notification.message")} ${contact.name}`,
			options
		);

		notification.onclick = e => {
			e.preventDefault();
			window.focus();
			navigateRef.current(`/tickets/${ticket.publicId || ticket.id}`);
		};

		setDesktopNotifications(prevState => {
			const notfiticationIndex = prevState.findIndex(
				n => n.tag === notification.tag
			);
			if (notfiticationIndex !== -1) {
				prevState[notfiticationIndex] = notification;
				return [...prevState];
			}
			return [notification, ...prevState];
		});

		soundAlertRef.current();
	};

	const handleClick = () => {
		setIsOpen(prevState => !prevState);
	};

	const handleClickAway = () => {
		setIsOpen(false);
	};

	const NotificationTicket = ({ children }) => {
		return <div onClick={handleClickAway}>{children}</div>;
	};

	return (
		<>
			<IconButton
				onClick={handleClick}
				ref={anchorEl}
				aria-label="Open Notifications"
				className={classes.iconButton}
			>
				<Badge badgeContent={notifications.length} className={classes.badge} overlap="rectangular">
					<ChatIcon />
				</Badge>
			</IconButton>
			<Popover
				disableScrollLock
				open={isOpen}
				anchorEl={anchorEl.current}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				classes={{ paper: classes.popoverPaper }}
				onClose={handleClickAway}
			>
				<List dense className={classes.tabContainer}>
					{notifications.length === 0 ? (
						<ListItem>
							<ListItemText>{i18n.t("notifications.noTickets")}</ListItemText>
						</ListItem>
					) : (
						notifications.map(ticket => (
							<NotificationTicket key={ticket.id}>
								<TicketListItem ticket={ticket} />
							</NotificationTicket>
						))
					)}
				</List>
			</Popover>
		</>
	);
};

export default NotificationsPopOver;
